<template>
  <div class="view-container user-area">
    <PageHeader title="ناحیه کاربری" infoTitle="راهنمای ناحیه کاربری" :infoLearnUrl="infoLearnUrl">
      <template #actions>
        <div class="header-actions">
          <BaseButton title="ویرایش پروفایل" variant="primary" type="button" @click="editProfile" />
          <BaseButton title="زیر سیستم ها" variant="success" type="button" @click="goToSubsystems" />
          <BaseButton title="تنظیمات" variant="secondary" type="button" @click="goToSettings" />
        </div>
      </template>
      <template #info>
        <p>
          در قسمت ناحیه کاربری امکان مشاهده تاریخ اعتبار پنل و تمدید آن، امکان مشاهده میزان فضای استفاده شده و اضافه
          کردن فضا، فعال سازی سرویس و زیرسیستم مد نظر، ویرایش حساب کاربری و تنظیمات امنیتی وجود دارد.
        </p>
        <p>
          همچنین برای مشاهده صورتحساب های پرداخت شده و ارتباط با پشتیبانی میتوانید بر روی هر کدام آز آیتم ها کلیک
          نمایید.
        </p>
      </template>
    </PageHeader>

    <div class="content-grid">
      <aside class="profile-card">
        <div class="company-image-upload">
          <div class="image-circle" @click="triggerImageUpload">
            <img :src="accountInfo.companyImageString || '/img/user-area1.svg'"
              :alt="accountInfo.companyImageString ? 'Company Logo' : 'Default Logo'"
              :class="{ 'default-logo': !accountInfo.companyImageString }" />
            <div class="edit-icon">
              <font-awesome-icon :icon="['fas', 'pencil-alt']" />
            </div>
          </div>
          <input ref="imageInput" type="file" accept="image/jpeg,image/jpg" @change="handleImageUpload"
            style="display: none;" />
        </div>
        <h2 class="name page-title">{{ companyTitle || 'کاربر محترم' }}</h2>
        <p class="subtitle">شماره پیشخوان: <strong>{{ panelNumber || '-' }}</strong></p>

        <!-- license box: نمایش تاریخ خرید، تاریخ اعتبار، وضعیت و دکمه تمدید زیر شماره پیشخوان -->
        <div class="license-box">
          <div class="license-row"><span class="label">تاریخ خرید :</span> <span class="value">{{
            toPersianDigits(purchaseDate) }}</span></div>
          <div class="license-row"><span class="label">تاریخ اعتبار :</span> <span class="value">{{
            toPersianDigits(expiryDate) }}</span></div>
          <div class="license-row"><span class="label">وضعیت :</span> <span class="value status">{{ licenseStatus
              }}</span>
          </div>
          <div class="license-actions">
            <BaseButton class="renew-btn" title="تمدید سامانه" variant="primary" type="button" @click="renewLicense" />
          </div>
        </div>

        <!-- Password change modal -->
        <transition name="password-modal">
          <div v-if="showPasswordModal" class="modal-overlay">
            <div class="modal-content" @click.stop>
              <ModalHeader>
                <template #default>
                  <h3>تغییر کلمه عبور</h3>
                </template>
                <template #actions>
                  <BaseButton class="close-btn" title="×" variant="ghost" type="button" @click="closePasswordModal" />
                </template>
              </ModalHeader>

              <div class="modal-body nicer-modal">
                <p class="modal-desc">برای تغییر کلمه عبور، کد تایید به شماره ثبت‌شده ارسال می‌شود. پس از دریافت کد، آن
                  را
                  وارد کرده و کلمه عبور جدید را انتخاب کنید.</p>

                <div class="current-mobile-row">
                  <div class="current-mobile-label">شماره ثبت‌شده</div>
                  <div class="current-mobile-value">{{ accountInfo.mobileNumber || 'ثبت نشده' }}</div>
                  <div class="current-mobile-action">
                    <BaseButton class="send-code-btn"
                      :title="passwordSendLoading ? 'در حال ارسال...' : (resendSecondsPass > 0 ? `ارسال مجدد (${resendSecondsPass}s)` : 'ارسال کد')"
                      variant="primary" type="button" @click="sendPasswordChangeCode"
                      :disabled="passwordSendLoading || resendSecondsPass > 0" />
                  </div>
                </div>

                <div v-if="passwordCodeSent" class="sent-info">کد تایید ارسال شد.</div>

                <div class="password-fields-vertical">
                  <div class="password-field">
                    <label>کد تایید</label>
                    <input class="rtl-input code-input" type="text" v-model="passwordVerificationCode" maxlength="6"
                      placeholder="______" inputmode="numeric" />
                  </div>

                  <div class="password-field">
                    <label>کلمه عبور جدید</label>
                    <input class="rtl-input new-mobile-input" type="password" v-model="passwordNew"
                      placeholder="کلمه عبور جدید" />
                  </div>

                  <div class="password-field">
                    <label>تکرار کلمه عبور</label>
                    <input class="rtl-input new-mobile-input" type="password" v-model="passwordConfirmNew"
                      placeholder="تکرار کلمه عبور" />
                  </div>
                </div>

                <div class="form-actions modal-actions">
                  <BaseButton title="تایید و ذخیره" variant="primary" type="button"
                    @click="submitPasswordChangeModal" />
                  <BaseButton title="انصراف" variant="cancel" @click="closePasswordModal" />
                </div>
              </div>
            </div>
          </div>
        </transition>

        <transition name="two-factor-modal">
          <div v-if="showTwoFactorMethodModal" class="modal-overlay">
            <div class="modal-content" @click.stop>
              <ModalHeader>
                <template #default>
                  <h3>انتخاب روش احراز هویت دو مرحله‌ای</h3>
                </template>
                <template #actions>
                  <BaseButton class="close-btn" title="×" variant="ghost" type="button"
                    @click="closeTwoFactorMethodModal" />
                </template>
              </ModalHeader>

              <div class="modal-body nicer-modal two-factor-modal-body">
                <p class="modal-desc">جهت تنظیم احراز هویت دو عاملی لطفا یکی از روش های زیر را انتخاب نمایید.</p>

                <div class="two-factor-method-list">
                  <label v-for="method in twoFactorMethods" :key="method.id" class="two-factor-method-item">
                    <div class="method-title-row">
                      <span v-if="!method.link">{{ method.label }}</span>
                      <a v-else :href="method.link" target="_blank" rel="noopener noreferrer" class="method-link">
                        {{ method.label }}
                      </a>
                    </div>
                    <div class="method-radio-wrap">
                      <input class="method-radio" type="radio" name="two-factor-method" :value="method.id"
                        v-model="selectedTwoFactorMethod" />
                      <span class="method-status">{{ selectedTwoFactorMethod === method.id ? 'فعال' : 'غیرفعال'
                      }}</span>
                    </div>
                  </label>
                </div>

                <div v-if="selectedTwoFactorMethod === 'google_authenticator'" class="google-2fa-setup">
                  <p class="setup-note">
                    برای فعال‌سازی، اپ Google Authenticator را باز کنید، QR را اسکن کنید و کد 6 رقمی را وارد کنید.
                  </p>

                  <div class="setup-actions">
                    <BaseButton :title="twoFactorLoading ? 'در حال دریافت...' : 'دریافت QR/Secret'" variant="secondary"
                      type="button" :disabled="twoFactorLoading" @click="startGoogleAuthenticatorSetup" />
                    <BaseButton v-if="twoFactorStatus.enabled"
                      :title="twoFactorLoading ? 'در حال غیرفعال‌سازی...' : 'غیرفعال‌سازی 2FA'" variant="cancel"
                      type="button" :disabled="twoFactorLoading" @click="disableTwoFactor" />
                  </div>

                  <div v-if="googleSetupData" class="google-setup-box">
                    <img v-if="googleSetupData.qrCodeImageUrl" :src="googleSetupData.qrCodeImageUrl"
                      alt="Google Authenticator QR" class="google-qr" />
                    <div v-else class="qr-placeholder">
                      QR بعد از آماده شدن بک‌اند از API دریافت می‌شود
                    </div>

                    <div class="manual-secret">
                      <strong>Secret:</strong>
                      <code>{{ googleSetupData.secret }}</code>
                    </div>

                    <input v-model="googleVerificationCode" class="rtl-input code-input two-factor-code-input"
                      type="text" maxlength="6" inputmode="numeric" placeholder="کد 6 رقمی" />
                  </div>

                  <p v-if="twoFactorError" class="two-factor-error">{{ twoFactorError }}</p>
                </div>

                <div class="form-actions modal-actions">
                  <BaseButton :title="twoFactorLoading ? 'در حال ثبت...' : 'تایید'" variant="primary" type="button"
                    :disabled="twoFactorLoading" @click="submitTwoFactorMethod" />
                  <BaseButton title="انصراف" variant="cancel" @click="closeTwoFactorMethodModal" />
                </div>
              </div>
            </div>
          </div>
        </transition>

        <div class="quick-stats">
          <div class="stat">
            <div class="value">{{ toPersianDigits(messages) }}</div>
            <div class="label">پیام‌ها</div>
          </div>
        </div>

        <div class="card-actions">
          <BaseButton title="صورتحساب‌ها" variant="outline" type="button" @click="openBilling" />
          <BaseButton title="پشتیبانی" variant="outline" type="button" @click="openSupport" />
        </div>
        <div class="version-box">
          <div class="version-row"><span class="version-label">آخرین نسخه:</span> <span class="version-value">{{
            latestVersion }}</span></div>
          <div class="version-row"><span class="version-label">نسخه شما:</span> <span class="version-value">{{
            currentVersion }}</span></div>
          <div class="version-actions">
            <BaseButton title="بررسی آخرین آپدیت" variant="ghost" type="button" @click="checkUpdate" />
          </div>
        </div>
      </aside>

      <section class="main-panel">
        <div class="panel-row">
          <div class="panel storage-panel">
            <h3>فضای ذخیره‌سازی</h3>
            <div class="storage-widget">
              <div class="donut">
                <svg viewBox="0 0 36 36" class="circular-chart">
                  <path class="circle-bg" d="M18 2.0845
                       a 15.9155 15.9155 0 0 1 0 31.831
                       a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path class="circle" :stroke-dasharray="(Math.round(storagePercent) || 0) + ', 100'" d="M18 2.0845
                       a 15.9155 15.9155 0 0 1 0 31.831
                       a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <text x="18" y="20.35" class="percentage">{{ toPersianDigits(Math.round(storagePercent)) }}%</text>
                </svg>
              </div>
              <div class="storage-info">
                <div class="used">استفاده‌شده: <strong>{{ toPersianDigits((usedStorage || 0).toFixed ?
                  Number(usedStorage).toFixed(2) : usedStorage) }} MB</strong></div>
                <div class="total">حجم کل: <strong>{{ toPersianDigits(totalStorage || 0) }} MB</strong></div>
                <BaseButton title="خرید حجم اضافه" variant="primary" type="button" @click="buyStorage" />
              </div>
            </div>
          </div>

          <div class="panel panel-service">
            <h3>سرویس فعال</h3>
            <div class="service-box">
              <div class="info-value" style="font-size:28px;">{{ toPersianDigits(activeServices) }}</div>
              <div class="info-sub">سرویس‌های فعال شما</div>
            </div>
          </div>
        </div>

        <!-- new row: recent account events (last settings update, last login) -->
        <div class="panel-row">
          <div class="panel">
            <h3>فایل‌های ضمیمه</h3>
            <div class="files-chart">
              <div class="files-info">تعداد فایل‌های ضمیمه: <strong>{{ toPersianDigits(attachedFilesCount) }}</strong>
              </div>
              <BaseButton title="مشاهده فایل‌ها" variant="outline" type="button" @click="goToFiles" />
            </div>
          </div>

          <div class="panel">
            <h3>رویدادهای حساب</h3>
            <div class="account-events">
              <div class="event-item">زمان آخرین ثبت تنظیمات: <strong>{{ lastSettingsUpdate || 'نامشخص' }}</strong>
              </div>
              <div class="event-item">آخرین ورود به سامانه: <strong>{{ lastLogin || 'نامشخص' }}</strong></div>
            </div>
          </div>
        </div>

        <div class="panel-row">
          <div class="panel wide">
            <h3>تنظیمات امنیتی</h3>
            <div class="security-grid">
              <div class="sec-item">
                <div class="label">تغییر کلمه عبور</div>
                <BaseButton title="تغییر" variant="outline" type="button" @click="openPasswordModal" />
              </div>
              <div class="sec-item">
                <div class="label">احراز هویت دو مرحله‌ای</div>
                <div class="two-factor-actions">
                  <span class="two-factor-badge" :class="{ active: twoFactorStatus.enabled }">
                    {{ twoFactorStatus.enabled ? `فعال (${twoFactorStatus.methodLabel})` : 'غیرفعال' }}
                  </span>
                  <BaseButton :title="twoFactorStatus.enabled ? 'مدیریت' : 'انتخاب'" variant="outline" type="button"
                    @click="openTwoFactorMethodModal" />
                </div>
              </div>
              <div class="sec-item">
                <div class="label">نشست‌های فعال</div>
                <BaseButton title="مدیریت" variant="ghost" type="button" @click="manageSessions" />
              </div>
            </div>
            <!-- Password change handled via modal -->
          </div>
        </div>

        <div class="panel-row">
          <div class="panel wide" ref="editAccountSection">
            <h3>ویرایش حساب کاربری</h3>
            <div class="account-edit-form">
              <div class="form-row user-name-row">
                <div class="form-group">
                  <label>نام:</label>
                  <input type="text" v-model="accountInfo.firstName" />
                </div>
                <div class="form-group">
                  <label>نام خانوادگی:</label>
                  <input type="text" v-model="accountInfo.lastName" />
                </div>
              </div>

              <div class="form-row company-header-row">
                <div class="form-group">
                  <label>عنوان شرکت/سازمان:</label>
                  <input type="text" v-model="accountInfo.title" />
                </div>
                <div class="form-group">
                  <label>رشته فعالیت/تخصص:</label>
                  <input type="text" v-model="accountInfo.description" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>نشانی:</label>
                  <input type="text" v-model="accountInfo.address" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>کد ملی:</label>
                  <input type="text" v-model="accountInfo.nationalCode" />
                </div>
                <div class="form-group">
                  <label>کد پستی:</label>
                  <input type="text" v-model="accountInfo.postalCode" />
                </div>
                <div class="form-group">
                  <label>کد اقتصادی:</label>
                  <input type="text" v-model="accountInfo.commercialCode" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>تلفن ثابت:</label>
                  <input type="text" v-model="accountInfo.phoneNumber" />
                </div>
                <div class="form-group">
                  <label>تلفن همراه:</label>
                  <div class="mobile-change-wrapper">
                    <input type="text" v-model="accountInfo.mobileNumber" disabled />
                    <!-- دکمه تغییر شماره موقتاً مخفی شده -->
                    <!-- (دکمه تغییر شماره موقتاً مخفی شده) -->
                  </div>
                </div>
              </div>
              <div class="form-actions">
                <BaseButton title="ذخیره تغییرات" variant="primary" type="button" @click="saveAccountInfo" />
              </div>
            </div>
          </div>
        </div>

        <!-- Subsystem purchase & activation box: placed at the bottom of the main panel -->
        <div class="panel-row">
          <div class="panel wide seubsystem-panl subsystem-panel">
            <h3>خرید و فعال سازی زیر سیستم</h3>
            <div class="subsystem-box">
              <ul class="subsystem-list">
                <li v-for="(t, i) in tariffItems" :key="t.id" class="subsystem-item">
                  <div class="item-left">
                    <span class="item-index">{{ toPersianDigits(i + 1) }}</span>
                    <div class="item-title">{{ t.title }}</div>
                  </div>
                  <div class="item-actions">
                    <div class="tariff-switch">
                      <span class="tariff-switch-text">{{ t.hasTariff ? t.paymentStatusString : 'انتخاب' }}</span>
                      <label class="switch" :aria-label="t.title">
                        <input v-if="t.hasTariff && t.paymentStatus !== 3" type="checkbox" checked disabled />
                        <input v-else type="checkbox" v-model="selectedTariffIds" :value="t.id"
                          :disabled="tariffLoading || showTariffInvoiceModal" />
                        <span class="slider"></span>
                      </label>
                    </div>
                  </div>
                </li>
              </ul>

              <div class="subsystem-footer">
                <div class="subsystem-hint">
                  <span v-if="tariffLoading">در حال دریافت تعرفه‌ها...</span>
                  <span v-else>برای خرید، تعرفه‌های مورد نظر را انتخاب کنید.</span>
                </div>
                <BaseButton title="مشاهده پیش‌فاکتور" variant="primary" type="button" @click="openTariffInvoiceModal"
                  :disabled="tariffLoading || selectedTariffIds.length === 0" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Tariff pre-invoice modal -->
    <transition name="tariff-modal">
      <div v-if="showTariffInvoiceModal" class="modal-overlay">
        <div class="modal-content" @click.stop>
          <ModalHeader>
            <template #default>
              <h3>پیش‌فاکتور خرید زیرسیستم‌ها</h3>
            </template>
            <template #actions>
              <BaseButton class="close-btn" title="×" variant="ghost" type="button" @click="cancelTariffPurchase" />
            </template>
          </ModalHeader>

          <div class="modal-body nicer-modal">
            <div v-if="selectedTariffItems.length === 0" class="invoice-empty">
              موردی برای نمایش وجود ندارد.
            </div>

            <div v-else class="invoice-list">
              <div v-for="t in selectedTariffItems" :key="t.id" class="invoice-row">
                <div class="invoice-title">{{ t.title }}</div>
                <div class="invoice-amount">{{ formatCurrency(t.amount) }}</div>
              </div>

              <div class="invoice-total">
                <div class="invoice-title">مجموع</div>
                <div class="invoice-amount">{{ formatCurrency(selectedTariffTotal) }}</div>
              </div>
            </div>

            <div class="form-actions modal-actions">
              <BaseButton title="تایید" variant="primary" type="button" @click="confirmTariffPurchase"
                :disabled="tariffLoading || selectedTariffIds.length === 0" />
              <BaseButton title="انصراف" variant="cancel" :disabled="tariffLoading" @click="cancelTariffPurchase" />
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Mobile change modal -->
    <div v-if="showMobileModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <ModalHeader>
          <template #default>
            <h3>تغییر شماره همراه</h3>
          </template>
          <template #actions>
            <BaseButton class="close-btn" title="×" variant="ghost" type="button" @click="closeMobileModal" />
          </template>
        </ModalHeader>

        <div class="modal-body nicer-modal">
          <p class="modal-desc">برای تغییر شماره همراه، ابتدا یک کد تایید به شماره فعلی ارسال خواهد شد. پس از دریافت کد،
            آن
            را وارد کرده و شماره جدید را ثبت کنید.</p>

          <div class="current-mobile-row">
            <div class="current-mobile-label">شماره فعلی</div>
            <div class="current-mobile-value">{{ accountInfo.mobileNumber || 'ثبت نشده' }}</div>
            <div class="current-mobile-action">
              <BaseButton class="send-code-btn"
                :title="mobileSendLoading ? 'در حال ارسال...' : (resendSeconds > 0 ? `ارسال مجدد (${resendSeconds}s)` : 'ارسال کد جهت تغییر شماره')"
                variant="primary" type="button" @click="sendMobileChangeCode"
                :disabled="mobileSendLoading || resendSeconds > 0" />
            </div>
          </div>

          <div v-if="mobileCodeSent" class="sent-info">کد تایید برای شماره فعلی ارسال شد.</div>

          <div class="form-row modal-fields">
            <div class="form-group inline-field">
              <div class="field-label"><span>کد تایید</span></div>
              <input class="rtl-input code-input" type="text" v-model="mobileVerificationCode" maxlength="6"
                placeholder="______" inputmode="numeric" />
            </div>

            <div class="form-group inline-field">
              <div class="field-label"><span>شماره جدید</span></div>
              <input class="rtl-input new-mobile-input" type="text" v-model="newMobileNumber" placeholder="۰۹۱۲..."
                inputmode="tel" />
            </div>
          </div>

          <div class="form-actions modal-actions">
            <BaseButton title="تایید و ذخیره" variant="primary" type="button" @click="submitMobileChange" />
            <BaseButton title="انصراف" variant="cancel" @click="closeMobileModal" />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import router from '@/router';
import { usePatientsStore } from '@/stores/clinic/patients';
import { useToast } from 'vue-toastification';
import moment from 'jalali-moment';
import { toPersianDigits, formatJalaliDate, formatCurrency } from '@/utils/formatters.js';
import apiClient from '@/axios';
import PageHeader from '@/components/PageHeader.vue'
import ModalHeader from '@/components/ModalHeader.vue'
import BaseButton from '@/components/UiCompunents/BaseButton.vue'
import { usetariffStore } from '@/stores/common/tariff'
import { usePepTransactionStore } from '@/stores/common/pepTransaction.js';
import { useAuthenticateStore } from '@/stores/common/authenticate';

const toast = useToast();
const patientsStore = usePatientsStore();
const tariffStore = usetariffStore();
const pepTransaction = usePepTransactionStore();
const authenticate = useAuthenticateStore();

const companyTitle = ref('');
const panelNumber = ref('');
const activeServices = ref(3);
const messages = ref(0);
const usedStorage = ref(0); // MB
const totalStorage = ref(1000); // MB
const storagePercent = computed(() => (totalStorage.value ? (usedStorage.value / totalStorage.value) * 100 : 0));
const recentActivities = ref([]);
const lastSettingsUpdate = ref('');
const lastLogin = ref('');
const attachedFilesCount = ref(0);
const attachmentsSeries = ref([2, 5, 3, 6, 4, 8, 7]);
const maxAttachments = computed(() => Math.max(...attachmentsSeries.value, 1));

// license / subscription info to display in profile card
const purchaseDate = ref('1402/09/20');
const expiryDate = ref('1404/10/01');
const licenseStatus = ref('دارای اعتبار');
// Info link for PageHeader help
const infoLearnUrl = 'https://www.sinapardazesh.ir/matab-learning-web/'

// version info
const currentVersion = ref('1.3.5');
const latestVersion = ref('1.3.5');

const patientsCount = ref(0);

const editAccountSection = ref(null);
const showChangePassword = ref(false);
const oldPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const showMobileModal = ref(false);
const mobileSendLoading = ref(false);
const mobileCodeSent = ref(false);
const mobileVerificationCode = ref('');
const newMobileNumber = ref('');
const generatedMobileCode = ref('');
const resendSeconds = ref(0);
let resendInterval = null;
const showPasswordModal = ref(false);
const passwordSendLoading = ref(false);
const submitPasswordChangeLoading = ref(false);
const passwordCodeSent = ref(false);
const passwordVerificationCode = ref('');
const passwordNew = ref('');
const passwordConfirmNew = ref('');
const generatedPasswordCode = ref('');
const resendSecondsPass = ref(0);
let resendIntervalPass = null;
const showTwoFactorMethodModal = ref(false);
const selectedTwoFactorMethod = ref('');
const twoFactorLoading = ref(false);
const twoFactorError = ref('');
const googleSetupData = ref(null);
const googleVerificationCode = ref('');
const twoFactorStatus = ref({
  enabled: false,
  method: '',
  methodLabel: ''
});
const twoFactorMethods = ref([
  { id: 'sms', label: 'پیامک', link: '' },
  {
    id: 'google_authenticator',
    label: 'Google Authenticator',
    link: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=fa'
  },
  { id: 'gmail', label: 'جیمیل', link: '' }
]);

function persistTwoFactorStatus() {
  localStorage.setItem('twoFactorStatus', JSON.stringify(twoFactorStatus.value));
}

function loadTwoFactorStatus() {
  try {
    const raw = localStorage.getItem('twoFactorStatus');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      twoFactorStatus.value = {
        enabled: Boolean(parsed.enabled),
        method: parsed.method || '',
        methodLabel: parsed.methodLabel || ''
      };
    }
  } catch (err) {
    console.warn('Unable to load twoFactorStatus from localStorage:', err);
  }
}

function resolveMethodLabel(methodId) {
  const found = twoFactorMethods.value.find(method => method.id === methodId);
  return found?.label || methodId || '';
}

function createMockGoogleSetup() {
  const secret = 'JBSWY3DPEHPK3PXP';
  const issuer = encodeURIComponent('Sinapack');
  const accountName = encodeURIComponent(accountInfo.value.mobileNumber || 'user@example.com');
  const otpAuthUri = `otpauth://totp/${issuer}:${accountName}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
  return {
    secret,
    otpAuthUri,
    qrCodeImageUrl: ''
  };
}

const monthlyBills = ref({ count: 0, total: 0 });
const monthStart = ref('');

const selectedTariffIds = ref([]);
const showTariffInvoiceModal = ref(false);
const tariffLoading = computed(() => tariffStore.loading);
const tariffItems = computed(() => (Array.isArray(tariffStore.tariffEntity) ? tariffStore.tariffEntity : []));

const selectedTariffItems = computed(() => {
  const ids = new Set(selectedTariffIds.value);
  return tariffItems.value.filter(t => ids.has(t.id));
});

const selectedTariffTotal = computed(() => {
  return selectedTariffItems.value.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
});

// account information for editing
const accountInfo = ref({
  firstName: '',
  lastName: '',
  title: '',
  description: '',
  companyImageString: '',
  companyImageSize: null,
  companyImageType: null,
  address: '',
  nationalCode: '',
  postalCode: '',
  phoneNumber: '',
  commercialCode: '',
  mobileNumber: ''
});

const imageInput = ref(null);

function triggerImageUpload() {
  imageInput.value?.click();
}

function handleImageUpload(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type (only JPG/JPEG)
  if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
    toast.error('آپلود عکس فقط با فرمت jpg و حجم کمتر از 200 کیلوبایت میسر می باشد.');
    event.target.value = ''; // Reset input
    return;
  }

  // Validate file size (max 200KB)
  if (file.size > 200 * 1024) {
    toast.error('آپلود عکس فقط با فرمت jpg و حجم کمتر از 200 کیلوبایت میسر می باشد.');
    event.target.value = ''; // Reset input
    return;
  }

  // Convert to base64
  const reader = new FileReader();
  reader.onload = (e) => {
    accountInfo.value.companyImageString = e.target?.result;
    accountInfo.value.companyImageSize = file.size;
    accountInfo.value.companyImageType = file.type;
  };
  reader.onerror = () => {
    toast.error('خطا در خواندن فایل');
    event.target.value = ''; // Reset input
  };
  reader.readAsDataURL(file);
}

async function confirmTariffPurchase() {
  if (selectedTariffIds.value.length === 0) {
    toast.error('لطفاً حداقل یک تعرفه را انتخاب کنید');
    return;
  }

  try {
    const result = await pepTransaction.requestPepTransaction(selectedTariffIds.value);

    if (result && Number(result.status) === 0 && result.redirectUrl) {
      showTariffInvoiceModal.value = false;
      selectedTariffIds.value = [];
      window.location.assign(result.redirectUrl);
      return;
    }

    toast.error('خطا در دریافت لینک پرداخت از بانک');
  } catch (e) {
    // toast handled in store
  }
}

function openTariffInvoiceModal() {
  if (selectedTariffIds.value.length === 0) {
    toast.error('لطفاً حداقل یک تعرفه را انتخاب کنید');
    return;
  }
  showTariffInvoiceModal.value = true;
}

function cancelTariffPurchase() {
  showTariffInvoiceModal.value = false;
  selectedTariffIds.value = [];
}

// convert Gregorian YYYY-MM-DD to Jalali jYYYY/jMM/jDD and Persian digits

onMounted(async () => {
  loadTwoFactorStatus();
  const c = JSON.parse(localStorage.getItem('companyInfo')) || {};
  companyTitle.value = c.title || '';
  panelNumber.value = c.panelNumber || '';

  const storageInfo = JSON.parse(localStorage.getItem('storageInfo')) || {};
  if (storageInfo) {
    usedStorage.value = storageInfo.totalSizeMB
    attachedFilesCount.value = storageInfo.fileCount
  }


  // sample recent activities (could be fetched)
  recentActivities.value = [
    { title: 'ورود به سامانه', time: 'یک ساعت پیش' },
    { title: 'به‌روزرسانی تنظیمات', time: '۳ روز پیش' },
  ];

  // populate last login / settings update from recent activities if available
  const loginAct = recentActivities.value.find(a => a.title.includes('ورود'));
  const settingsAct = recentActivities.value.find(a => a.title.includes('به‌روزرسانی') || a.title.includes('تنظیمات'));
  lastLogin.value = loginAct ? loginAct.time : '';
  lastSettingsUpdate.value = settingsAct ? settingsAct.time : '';

  // try to fetch patients count (best-effort)
  try {
    await patientsStore.fetchPatients(1);
    patientsCount.value = patientsStore.pagination?.total || patientsStore.patientsList.length || 0;
  } catch (err) {
    // ignore
  }

  // load tariffs for subsystem purchase/activation
  try {
    await tariffStore.fetchtariff();
  } catch (e) {
    // best-effort
  }

  // sample monthly bills data (could be fetched from API)
  // using requested demo values
  monthStart.value = '2025-10-31';
  monthlyBills.value = { count: 12, total: 1250000 };

  // Load account info from companyInfo or initialize with default values
  accountInfo.value = {
    firstName: c.firstName || '',
    lastName: c.lastName || '',
    title: c.title || 'دمو اتوماسیون سیناپک',
    description: c.description || 'SinapardazeshSoft',
    companyImageString: c.companyImageString || '',
    companyImageSize: c.companyImageSize || null,
    companyImageType: c.companyImageType || null,
    address: c.address || 'تهران، سهروردی شمالی',
    nationalCode: c.nationalCode || '11111111',
    postalCode: c.postalCode || '11111111',
    phoneNumber: c.phoneNumber || '02188745474',
    commercialCode: c.commercialCode || '11111111',
    mobileNumber: c.mobileNumber || '09213560531'
  };
});

// (helper navigation function removed — not used after quick-links were removed)
function editProfile() {
  // Scroll to edit account section
  if (editAccountSection.value) {
    editAccountSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
function goToSettings() { router.push('/overview/common/app-settings?tab=general'); }
function goToSubsystems() {
  // Scroll to subsystems section
  const subsystemsSection = document.querySelector('.subsystem-panel');
  if (subsystemsSection) {
    subsystemsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
function openBilling() { router.push('/overview/common/paid-invoices'); }
function openSupport() { router.push('/overview/common/messenger'); }
function manageSessions() { /* navigate or show modal */ }

function buyStorage() {
  // navigate to billing/purchase page
  router.push('/overview/common/app-settings?tab=billing');
}

function renewLicense() {
  // navigate user to billing/renew page or show toast
  // this is a best-effort navigation; adjust route as needed in your app
  router.push('/overview/common/app-settings?tab=billing');
  toast.info('در حال انتقال به صفحه تمدید...');
}

function checkUpdate() {
  // In a real app you might fetch an endpoint to get the latest published version.
  // here we'll simulate comparison and show the user a toast.
  if (latestVersion.value === currentVersion.value) {
    toast.info('نسخه شما به روز است');
  } else {
    toast.info('نسخه جدید در دسترس است: ' + latestVersion.value);
    // optional: navigate to a release page if available
    // router.push('/overview/common/app-settings?tab=billing');
  }
}

function goToFiles() {
  // best-effort navigation to patient files manager or patients page
  router.push('/overview/clinic/clinic-patients');
}

function openMonthlyBills() {
  // open reports with query params from start of month
  router.push({ path: '/overview/clinic/clinic-reports', query: { report: 'bills', from: monthStart.value } });
}

function submitChangePassword() {
  if (!oldPassword.value || !newPassword.value) {
    toast.error('لطفاً فیلدها را تکمیل کنید');
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    toast.error('کلمه‌های عبور مطابقت ندارند');
    return;
  }

  // Here you would call API to change password. We'll simulate success.
  setTimeout(() => {
    toast.success('کلمه عبور با موفقیت تغییر کرد');
    showChangePassword.value = false;
    oldPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
  }, 700);
}

function saveAccountInfo() {
  // Validate required fields
  if (!accountInfo.value.title) {
    toast.error('لطفاً عنوان شرکت را وارد کنید');
    return;
  }

  // Save to localStorage (or call API endpoint)
  const companyInfo = JSON.parse(localStorage.getItem('companyInfo')) || {};
  const updatedInfo = {
    ...companyInfo,
    ...accountInfo.value
  };

  localStorage.setItem('companyInfo', JSON.stringify(updatedInfo));

  // Update the display title
  companyTitle.value = accountInfo.value.title;

  // Here you would call API to save the account info
  // Example: await api.updateCompanyInfo(accountInfo.value);

  toast.success('اطلاعات حساب کاربری با موفقیت ذخیره شد');
}

function openMobileModal() {
  showMobileModal.value = true;
  mobileVerificationCode.value = '';
  newMobileNumber.value = '';
  mobileCodeSent.value = false;
  generatedMobileCode.value = '';
}

function openPasswordModal() {
  showPasswordModal.value = true;
  passwordVerificationCode.value = '';
  passwordNew.value = '';

  passwordCodeSent.value = false;
  generatedPasswordCode.value = '';
}

function closePasswordModal() {
  showPasswordModal.value = false;
  cleanupPasswordModal();
}

function openTwoFactorMethodModal() {
  openTwoFactorMethodModalEx();
}

function closeTwoFactorMethodModal() {
  closeTwoFactorMethodModalEx();
}

async function submitTwoFactorMethod() {
  if (googleSetupData.value !== undefined) {
    return submitTwoFactorMethodEx();
  }
  if (!selectedTwoFactorMethod.value) {
    toast.error('لطفاً یکی از روش‌های احراز هویت را انتخاب کنید');
    return;
  }
  const selectedMethod = twoFactorMethods.value.find(method => method.id === selectedTwoFactorMethod.value);
  toast.success(`روش ${selectedMethod?.label || 'انتخاب‌شده'} ثبت شد`);
  closeTwoFactorMethodModal();
}

function openTwoFactorMethodModalEx() {
  showTwoFactorMethodModal.value = true;
  twoFactorError.value = '';
  googleVerificationCode.value = '';
  if (twoFactorStatus.value.method) {
    selectedTwoFactorMethod.value = twoFactorStatus.value.method;
  }
}

function closeTwoFactorMethodModalEx() {
  showTwoFactorMethodModal.value = false;
  twoFactorLoading.value = false;
  twoFactorError.value = '';
  googleVerificationCode.value = '';
  googleSetupData.value = null;
}

async function startGoogleAuthenticatorSetup() {
  twoFactorError.value = '';
  twoFactorLoading.value = true;
  try {
    const response = await apiClient.post('/Authenticate/TwoFactor/Google/Setup', {});
    const data = response?.data || {};
    googleSetupData.value = {
      secret: data.secret || '',
      otpAuthUri: data.otpAuthUri || '',
      qrCodeImageUrl: data.qrCodeImageUrl || ''
    };

    if (!googleSetupData.value.secret) {
      throw new Error('اطلاعات Google Authenticator از سرور دریافت نشد.');
    }
  } catch (err) {
    googleSetupData.value = createMockGoogleSetup();
    toast.info('بک‌اند آماده نیست؛ فعلا از حالت نمایشی فرانت استفاده شد.');
  } finally {
    twoFactorLoading.value = false;
  }
}

async function disableTwoFactor() {
  twoFactorError.value = '';
  twoFactorLoading.value = true;
  try {
    try {
      await apiClient.post('/Authenticate/TwoFactor/Disable', {});
    } catch (err) {
      // بک‌اند هنوز آماده نیست؛ در فرانت وضعیت را تغییر می‌دهیم.
    }

    twoFactorStatus.value = {
      enabled: false,
      method: '',
      methodLabel: ''
    };
    persistTwoFactorStatus();
    selectedTwoFactorMethod.value = '';
    googleVerificationCode.value = '';
    googleSetupData.value = null;
    toast.success('احراز هویت دو مرحله‌ای غیرفعال شد');
  } finally {
    twoFactorLoading.value = false;
  }
}

async function submitTwoFactorMethodEx() {
  twoFactorError.value = '';
  if (!selectedTwoFactorMethod.value) {
    toast.error('لطفاً یکی از روش‌های احراز هویت را انتخاب کنید');
    return;
  }

  if (selectedTwoFactorMethod.value !== 'google_authenticator') {
    const selectedMethod = twoFactorMethods.value.find(method => method.id === selectedTwoFactorMethod.value);
    twoFactorStatus.value = {
      enabled: true,
      method: selectedTwoFactorMethod.value,
      methodLabel: selectedMethod?.label || ''
    };
    persistTwoFactorStatus();
    toast.success(`روش ${selectedMethod?.label || 'انتخاب‌شده'} ثبت شد`);
    closeTwoFactorMethodModalEx();
    return;
  }

  if (!googleSetupData.value?.secret) {
    twoFactorError.value = 'ابتدا QR/Secret را دریافت کنید.';
    return;
  }

  if (!/^\d{6}$/.test(googleVerificationCode.value)) {
    twoFactorError.value = 'کد تایید باید 6 رقمی باشد.';
    return;
  }

  twoFactorLoading.value = true;
  try {
    try {
      await apiClient.post('/Authenticate/TwoFactor/Google/VerifySetup', {
        code: googleVerificationCode.value,
        secret: googleSetupData.value.secret
      });
    } catch (err) {
      // بک‌اند هنوز آماده نیست؛ فرانت را برای فاز توسعه فعال می‌کنیم.
    }

    twoFactorStatus.value = {
      enabled: true,
      method: 'google_authenticator',
      methodLabel: resolveMethodLabel('google_authenticator')
    };
    persistTwoFactorStatus();
    toast.success('Google Authenticator با موفقیت فعال شد');
    closeTwoFactorMethodModalEx();
  } finally {
    twoFactorLoading.value = false;
  }
}

async function sendPasswordChangeCode() {
  passwordSendLoading.value = true;
  try {
    const payload = {
      mobileNumber: accountInfo.value.mobileNumber
    }
    await authenticate.sendOtpForChangePassword(payload);

  } catch (e) {
    // toast handled in store
  } finally {
    passwordSendLoading.value = false;
    passwordCodeSent.value = true;
  }
}

async function submitPasswordChangeModal() {
  if (!passwordCodeSent.value) { toast.error('لطفاً ابتدا کد را ارسال کنید'); return; }
  if (!passwordVerificationCode.value) { toast.error('کد تایید را وارد کنید'); return; }
  if (!passwordNew.value || passwordNew.value.length < 6) { toast.error('کلمه عبور جدید باید حداقل ۶ کاراکتر باشد'); return; }
  if (!passwordConfirmNew.value) { toast.error('لطفاً تکرار کلمه عبور را وارد کنید'); return; }
  if (passwordNew.value !== passwordConfirmNew.value) { toast.error('کلمه‌های عبور مطابقت ندارند'); return; }
  submitPasswordChangeLoading.value = true;
  try {
    const payload = {
      newPassword: passwordNew.value,
      otpCode: passwordVerificationCode.value,

    }
    await authenticate.changePassword(payload);

  } catch (e) {
    // toast handled in store
  } finally { submitPasswordChangeLoading.value = false }
}

function cleanupPasswordModal() {
  if (resendIntervalPass) { clearInterval(resendIntervalPass); resendIntervalPass = null; }
  passwordSendLoading.value = false;
  submitPasswordChangeLoading.value = false;
  passwordCodeSent.value = false;
  passwordVerificationCode.value = '';
  passwordNew.value = '';
  passwordConfirmNew.value = '';
  generatedPasswordCode.value = '';
  resendSecondsPass.value = 0;
}

function closeMobileModal() {
  showMobileModal.value = false;
  cleanupMobileModal();
}

// cleanup when closing
function cleanupMobileModal() {
  if (resendInterval) { clearInterval(resendInterval); resendInterval = null; }
  resendSeconds.value = 0;
  mobileSendLoading.value = false;
  mobileCodeSent.value = false;
  mobileVerificationCode.value = '';
  newMobileNumber.value = '';
  generatedMobileCode.value = '';
}

function sendMobileChangeCode() {
  if (!accountInfo.value.mobileNumber) {
    toast.error('شماره موبایل فعلی نامعتبر است');
    return;
  }
  mobileSendLoading.value = true;
  // simulate sending SMS
  setTimeout(() => {
    mobileSendLoading.value = false;
    mobileCodeSent.value = true;
    generatedMobileCode.value = String(Math.floor(100000 + Math.random() * 900000));
    // In real app you wouldn't show the code in UI; for demo show a brief info
    toast.info('کد تایید ارسال شد');
    // start resend countdown
    resendSeconds.value = 60;
    if (resendInterval) clearInterval(resendInterval);
    resendInterval = setInterval(() => {
      resendSeconds.value -= 1;
      if (resendSeconds.value <= 0) {
        clearInterval(resendInterval);
        resendInterval = null;
      }
    }, 1000);
  }, 900);
}

function submitMobileChange() {
  if (!mobileCodeSent.value) {
    toast.error('لطفاً ابتدا کد تغییر را ارسال کنید');
    return;
  }
  if (!mobileVerificationCode.value) {
    toast.error('لطفاً کد تایید را وارد کنید');
    return;
  }
  if (mobileVerificationCode.value !== generatedMobileCode.value) {
    toast.error('کد تایید اشتباه است');
    return;
  }
  if (!newMobileNumber.value) {
    toast.error('لطفاً شماره جدید را وارد کنید');
    return;
  }

  // Update local state and persist
  accountInfo.value.mobileNumber = newMobileNumber.value;
  const companyInfo = JSON.parse(localStorage.getItem('companyInfo')) || {};
  const updatedInfo = { ...companyInfo, ...accountInfo.value };
  localStorage.setItem('companyInfo', JSON.stringify(updatedInfo));
  toast.success('شماره همراه با موفقیت تغییر کرد');
  cleanupMobileModal();
  showMobileModal.value = false;
}

// simple currency filter replacement: format number with thousands separator
// formatters moved to src/utils/formatters.js
const currency = formatCurrency;
</script>

<style scoped>
.user-area {
  padding: 20px;
  direction: rtl;
  text-align: right;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.header {
  margin: 0 0 12px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.content-grid {
  display: flex;
  gap: 20px;
  margin-top: 18px;
  align-items: flex-start;
}

.profile-card {
  width: 260px;
  background: var(--background-color-secondary);
  padding: 18px;
  border-radius: 12px;
  box-shadow: var(--box-shadow);
}

.avatar {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  margin: 0 auto 8px;
  color: var(--text-color-secondary);
}

.name {
  text-align: center;
  margin: 6px 0;
}

.subtitle {
  text-align: center;
  color: var(--text-color-secondary);
  font-size: 13px;
}

.license-box {
  margin: 12px 0 6px;
  padding: 10px;
  background: var(--background-color);
  border-radius: 8px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.06);
  text-align: right;
}

.license-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
  color: var(--text-color-secondary);
}

.license-row .label {
  color: var(--text-color-secondary);
}

.license-row .value {
  font-weight: 700;
  color: var(--text-color-primary);
}

.license-row .status {
  color: #1a9b3a;
}

.license-actions {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.renew-btn {
  width: 100%;
}

.quick-stats {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.stat {
  flex: 1;
  text-align: center;
  background: var(--background-color);
  padding: 10px;
  border-radius: 8px;
}

.stat .value {
  font-weight: 700;
}

.card-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
}

.version-box {
  margin-top: 12px;
  padding: 10px;
  border-radius: 8px;
  background: var(--background-color);
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.06);
  text-align: right;
}

.version-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
  color: var(--text-color-secondary);
}

.version-row .version-label {
  color: var(--text-color-secondary);
}

.version-row .version-value {
  font-weight: 700;
  color: var(--text-color-primary);
}

.version-actions {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}

.main-panel {
  flex: 1;
  min-width: 0;
  /* allow flex-item to shrink and prevent horizontal overflow */
}

.panel-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

/* Mobile: stack panels vertically (one per row) */
@media (max-width: 640px) {
  .panel-row {
    flex-direction: column;
  }

  .panel {
    width: 100%;
  }
}

.panel {
  background: var(--background-color);
  padding: 16px;
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  flex: 1;
}

.panel.wide {
  min-height: 120px;
}

.quick-links {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-link {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--background-color);
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.06);
  cursor: pointer;
  flex-direction: row-reverse;
}

.activity-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.activity-list li {
  padding: 8px 0;
  border-bottom: 1px dashed rgba(var(--text-color-secondary-rgb), 0.12);
  display: flex;
  justify-content: space-between;
}

.security-grid {
  display: flex;
  gap: 12px;
  align-items: center;
}

.sec-item {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: var(--background-color);
  border-radius: 8px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
}

/* Security section labels (تغییر کلمه عبور / احراز هویت دو مرحله‌ای / نشست‌های فعال) */
.sec-item .label {
  font-size: 12px;
  line-height: 1.3;
}

.two-factor-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.two-factor-badge {
  font-size: 12px;
  border-radius: 999px;
  padding: 4px 10px;
  background: rgba(var(--text-color-secondary-rgb), 0.1);
  color: var(--text-color-secondary);
}

.two-factor-badge.active {
  color: #166534;
  background: rgba(22, 101, 52, 0.12);
}

@media (max-width: 640px) {
  .security-grid {
    flex-direction: column;
    align-items: stretch;
  }

  .sec-item {
    width: 100%;
  }

  .sec-item .label {
    font-size: 11px;
  }

  .two-factor-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.two-factor-method-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.two-factor-method-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.18);
  border-radius: 8px;
  background: var(--background-color);
}

.method-title-row {
  display: flex;
  align-items: center;
}

.method-link {
  color: var(--background-color-button);
  text-decoration: underline;
}

.method-radio-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.method-radio {
  width: 16px;
  height: 16px;
  accent-color: var(--background-color-button);
}

.method-status {
  font-size: 12px;
  color: var(--text-color-secondary);
}

.google-2fa-setup {
  margin-top: 12px;
  border: 1px dashed rgba(var(--text-color-secondary-rgb), 0.25);
  border-radius: 8px;
  padding: 12px;
}

.setup-note {
  font-size: 13px;
  color: var(--text-color-secondary);
  margin-bottom: 10px;
}

.setup-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.google-setup-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.google-qr {
  width: 180px;
  height: 180px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.15);
  border-radius: 8px;
  background: #fff;
  object-fit: contain;
}

.qr-placeholder {
  width: 180px;
  min-height: 180px;
  border: 1px dashed rgba(var(--text-color-secondary-rgb), 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  color: var(--text-color-secondary);
  padding: 12px;
}

.manual-secret {
  direction: ltr;
  text-align: center;
  font-size: 12px;
}

.manual-secret code {
  display: inline-block;
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(var(--text-color-secondary-rgb), 0.1);
}

.two-factor-code-input {
  width: 180px;
}

.two-factor-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 8px;
}

/* smooth open/close animation for password modal */
.password-modal-enter-active,
.password-modal-leave-active {
  transition: opacity 0.24s ease;
}

.password-modal-enter-active .modal-content,
.password-modal-leave-active .modal-content {
  transition: transform 0.24s ease, opacity 0.24s ease;
}

.password-modal-enter-from,
.password-modal-leave-to {
  opacity: 0;
}

.password-modal-enter-from .modal-content,
.password-modal-leave-to .modal-content {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

/* smooth open/close animation for tariff pre-invoice modal */
.tariff-modal-enter-active,
.tariff-modal-leave-active {
  transition: opacity 0.24s ease;
}

.tariff-modal-enter-active .modal-content,
.tariff-modal-leave-active .modal-content {
  transition: transform 0.24s ease, opacity 0.24s ease;
}

.tariff-modal-enter-from,
.tariff-modal-leave-to {
  opacity: 0;
}

.tariff-modal-enter-from .modal-content,
.tariff-modal-leave-to .modal-content {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

/* smooth open/close animation for two-factor modal only */
.two-factor-modal-enter-active,
.two-factor-modal-leave-active {
  transition: opacity 0.24s ease;
}

.two-factor-modal-enter-active .modal-content,
.two-factor-modal-leave-active .modal-content {
  transition: transform 0.24s ease, opacity 0.24s ease;
}

.two-factor-modal-enter-from,
.two-factor-modal-leave-to {
  opacity: 0;
}

.two-factor-modal-enter-from .modal-content,
.two-factor-modal-leave-to .modal-content {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (max-width: 900px) {
  .content-grid {
    flex-direction: column;
  }

  .main-panel {
    width: 100%;
    min-width: 0;
  }

  .profile-card {
    width: 100%;
  }
}

/* storage donut and widgets */
.storage-widget {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
}

.circular-chart {
  width: 68px;
  height: 68px;
}

.circular-chart .circle-bg {
  fill: none;
  stroke: rgba(var(--text-color-secondary-rgb), 0.18);
  stroke-width: 3.5;
}

.circular-chart .circle {
  fill: none;
  stroke: var(--background-color-button);
  stroke-width: 3.5;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

.circular-chart .percentage {
  font-size: 8px;
  text-anchor: middle;
  fill: var(--text-color-primary);
}

.storage-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.storage-info .used,
.storage-info .total {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.files-chart {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.bar-chart {
  width: 180px;
  height: 60px;
}

.files-info {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.change-password-panel {
  margin-top: 12px;
  padding: 12px;
  background: var(--background-color);
  border-radius: 8px;
}

.change-password-panel .form-group {
  margin-bottom: 8px;
}

.change-password-panel input {
  width: 100%;
  padding: 8px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  border-radius: 6px;
  text-align: right;
  background: var(--background-color);
  color: var(--text-color-primary);
}

.change-password-panel label {
  display: block;
  text-align: right;
  margin-bottom: 4px;
}

.change-password-panel .form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.account-edit-form {
  margin-top: 12px;
}

.account-edit-form .form-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.account-edit-form .company-header-row {
  align-items: flex-start;
}

.company-image-upload {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.image-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--background-color-secondary);
  border: 2px solid rgba(var(--text-color-secondary-rgb), 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  /* allow the edit badge to sit on the edge */
  overflow: visible;
  transition: all 0.3s ease;
}

.image-circle:hover {
  border-color: var(--background-color-button);
  transform: scale(1.05);
}

.image-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.image-circle .default-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
}

.image-circle .placeholder-icon {
  font-size: 32px;
  color: var(--text-color-secondary);
}

.image-circle .edit-icon {
  position: absolute;
  right: -6px;
  bottom: -6px;
  background: linear-gradient(135deg, var(--background-color-button), rgba(var(--background-color-button-rgb), 0.9));
  color: #fff;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border: 3px solid var(--background-color);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
  transform: translateZ(0);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  /* keep the whole avatar clickable for upload */
  pointer-events: none;
}

.image-circle:hover .edit-icon {
  transform: scale(1.06);
  filter: brightness(1.03);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.22);
}

.image-circle .edit-icon :deep(svg) {
  width: 14px;
  height: 14px;
}

.account-edit-form .form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.account-edit-form label {
  display: block;
  text-align: right;
  margin-bottom: 4px;
  font-size: 13px;
  color: var(--text-color-secondary);
}

.account-edit-form input {
  width: 100%;
  padding: 8px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  border-radius: 6px;
  text-align: right;
  background: var(--background-color);
  color: var(--text-color-primary);
}

.mobile-change-wrapper {
  position: relative;
}

.mobile-change-wrapper input[disabled] {
  background: #f3f4f6;
  /* gray */
  color: var(--text-color-secondary);
  opacity: 1;
  cursor: default;
}

.mobile-change-btn {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px 10px;
  background: transparent;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.08);
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: var(--text-color-secondary);
}

.account-edit-form .form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

@media (max-width: 768px) {
  .account-edit-form .form-row {
    flex-direction: column;
  }

  /* Mobile: make company title/description fields full width like other fields */
  .account-edit-form .company-header-row {
    flex-direction: column;
    align-items: stretch;
  }

  .account-edit-form .company-header-row .form-group {
    width: 100%;
  }
}

.bills-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mini-stats {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.mini-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--background-color);
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.06);
  min-width: 140px;
}

.mini-stat .label {
  font-size: 13px;
  color: var(--text-color-secondary);
  margin-left: 8px;
}

.mini-stat .value {
  font-weight: 700;
  color: var(--text-color-primary);
}

/* Modal styles (same pattern as other pages) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  width: 480px;
  max-width: 95%;
  background: var(--background-color);
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-body {
  padding: 16px;
}

.close-btn:deep(.base-button) {
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 6px;
  min-width: 34px;
}

.send-code-btn:deep(.base-button) {
  min-width: 160px;
}

.nicer-modal .modal-desc {
  margin-bottom: 12px;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.current-mobile-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  margin-bottom: 12px;
}

.current-mobile-label {
  color: var(--text-color-secondary);
  font-size: 13px;
  min-width: 80px;
}

.current-mobile-value {
  font-weight: 700;
  color: var(--text-color-primary);
}

.current-mobile-action {
  margin-left: auto;
}

.sent-info {
  color: #16a34a;
  font-size: 13px;
  margin-bottom: 8px;
}

.modal-fields {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.modal-fields .form-group {
  flex: 1;
}

.code-input {
  max-width: 120px;
}

.modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

/* Field label and input styling for nicer look */
.field-label {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.field-label span {
  font-weight: 400;
  color: var(--text-color-secondary);
}

.field-hint {
  font-size: 12px;
  color: var(--text-color-secondary);
}

.inline-field {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
  min-width: 0;
  direction: ltr;
}

.inline-field .field-label {
  margin-bottom: 0;
  width: auto;
  flex: 0 0 auto;
  text-align: left;
  padding: 0 4px;
}

.inline-field input {
  flex: 1;
  min-width: 0;
}

/* password modal specific: make labels and inputs right aligned */
.password-inline {
  direction: rtl;
}

.password-inline .field-label {
  text-align: right;
  padding: 0 6px;
}

.password-inline input {
  text-align: right;
}

.password-inline .code-input {
  /* keep dashed placeholder look like desktop modal */
  text-align: center;
  font-weight: 700;
  letter-spacing: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New', monospace;
  font-size: 18px;
}

@media (max-width: 700px) {
  .modal-fields {
    flex-direction: column;
  }

  .inline-field {
    flex-direction: column;
    align-items: stretch;
  }

  .inline-field .field-label {
    width: auto;
    flex: none;
    margin-bottom: 6px;
    text-align: right;
  }
}

.modal-body input {
  padding: 10px 12px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  border-radius: 8px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  transition: box-shadow .15s ease, border-color .15s ease;
}

.modal-body input:focus {
  outline: none;
  border-color: rgba(59, 130, 246, 0.9);
  /* blue-500 */
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.08);
}

.code-input {
  text-align: center;
  font-weight: 700;
  letter-spacing: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New', monospace;
  font-size: 18px;
}

.new-mobile-input {
  font-size: 15px;
}

.password-fields-vertical {
  display: flex;
  flex-direction: column;
  /* stack fields vertically */
  align-items: flex-end;
  /* align fields to the right */
  gap: 12px;
  margin-top: 8px;
  width: 100%;
}

.password-field {
  width: 420px;
  max-width: 100%;
  display: flex;
  align-items: center;
}

.password-field label {
  width: 120px;
  flex: 0 0 120px;
  text-align: right;
  color: var(--text-color-secondary);
  margin: 0;
  padding-right: 6px;
}

.password-field input {
  flex: 1;
  min-width: 0;
}

.password-field .code-input {
  /* ensure code field matches width */
  width: 100%;
  text-align: center;
}

.top-stats {
  display: flex;
  gap: 18px;
  margin-bottom: 12px;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: flex-end;
  width: 100%;
}

.top-stats .password-fields-vertical {
  flex-direction: column;
  align-items: center;
}

.top-stats .password-field {
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.top-stats .password-field label {
  width: auto;
  text-align: right;
  margin-bottom: 6px;
}

.info-box {
  flex: 1 1 320px;
  min-width: 200px;
  max-width: none;
  background: var(--background-color);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  box-shadow: none;
}

.info-box .box-title,
.info-box .info-label,
.info-box .info-value,
.info-box .info-sub {
  width: 100%;
  text-align: right;
  display: block;
}

.info-label {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.info-value {
  font-weight: 800;
  color: var(--text-color-primary);
  font-size: 20px;
}

.info-sub {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.info-actions {
  width: 100%;
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 700px) {
  .top-stats {
    flex-direction: column;
  }
}

.box-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-color-primary);
  margin-bottom: 6px;
  text-align: right;
  width: 100%;
}

/* right-align info-box content for RTL layout */
.info-box,
.info-box .info-label,
.info-box .info-value,
.info-box .info-sub {
  text-align: right;
  align-items: flex-end;
}

.panel-service .service-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 6px;
}

.panel-service .info-value {
  font-size: 28px;
}

/* storage-panel styles — mirror the look of panel-service but two-column friendly */
.storage-panel .storage-widget {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.storage-panel .storage-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.storage-panel .used,
.storage-panel .total {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.account-events {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.account-events .event-item {
  font-size: 13px;
  color: var(--text-color-secondary);
}

/* dark-mode: ensure these action buttons have white text for contrast */
/* (btn overrides removed: BaseButton has its own styling) */

/* subsystem panel styling */
.subsystem-panel {
  padding-bottom: 6px;
}

.subsystem-box {
  margin-top: 8px;
}

.subsystem-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.subsystem-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  background: var(--background-color);
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.06);
}

.subsystem-item .item-left {
  display: flex;
  gap: 10px;
  align-items: center;
}

.subsystem-item .item-index {
  background: rgba(var(--text-color-secondary-rgb), 0.04);
  padding: 6px 8px;
  border-radius: 6px;
  font-weight: 700;
}

.subsystem-item .item-title {
  font-weight: 600;
  color: var(--text-color-primary);
}

.subsystem-item .item-actions {
  display: flex;
  gap: 8px;
}

.tariff-switch {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  user-select: none;
}

.tariff-switch-text {
  font-size: 13px;
  color: var(--text-color-secondary);
}

/* Switch (checkbox styled as toggle) */
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  direction: ltr;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: rgba(var(--text-color-secondary-rgb), 0.25);
  transition: 0.2s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  top: 3px;
  background-color: #fff;
  transition: 0.2s;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.switch input:checked+.slider {
  background-color: var(--background-color-button);
}

.switch input:checked+.slider:before {
  transform: translateX(22px);
}

.switch input:disabled+.slider {
  cursor: not-allowed;
  opacity: 0.7;
}

.subsystem-footer {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.subsystem-hint {
  font-size: 12px;
  color: var(--text-color-secondary);
}

.invoice-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.invoice-row,
.invoice-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  direction: rtl;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.08);
  background: var(--background-color);
}

.invoice-total {
  border-color: rgba(59, 130, 246, 0.28);
  background: rgba(59, 130, 246, 0.06);
}

.invoice-title {
  font-weight: 700;
  color: var(--text-color-primary);
  text-align: right;
}

.invoice-amount {
  font-weight: 800;
  color: var(--text-color-primary);
  text-align: left;
  white-space: nowrap;
}

.invoice-empty {
  width: 100%;
  color: var(--text-color-secondary);
  font-size: 13px;
  text-align: right;
}

@media (max-width: 700px) {
  .subsystem-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .subsystem-item .item-actions {
    align-self: stretch;
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}

/* Light-mode (non dark-theme) — use requested blue for buttons and charts */
/* (btn overrides removed: BaseButton has its own styling) */

/* Charts color */
:deep(body:not(.dark-theme)) .user-area .bar-chart rect {
  fill: #1474f2;
}

:deep(body:not(.dark-theme)) .user-area .bar-chart rect:hover {
  fill: #1c3f79;
}

:deep(body:not(.dark-theme)) .user-area .circular-chart .circle {
  stroke: #1474f2;
}

:deep(body:not(.dark-theme)) .user-area .circular-chart .circle:hover {
  stroke: #1c3f79;
}

/* Local CSS variable overrides inside this view so components that use
   var(--text-color-primary) or --background-color-button pick up the blue
   only for this page in light mode. This avoids changing global theme vars. */
:deep(body:not(.dark-theme)) .user-area {
  --text-color-primary: #1474f2;
  --background-color-button: #1474f2;
  --background-color-button-rgb: 20, 116, 242;
  --background-color-button-hover: #1c3f79;
}

/* Stronger overrides to beat global selectors using var(--text-color-primary) */
/* (btn overrides removed: BaseButton has its own styling) */

/* Ensure SVG/chart elements follow the same blue in this view */
:deep(body:not(.dark-theme)) .user-area .bar-chart rect {
  fill: #1474f2 !important;
}

:deep(body:not(.dark-theme)) .user-area .bar-chart rect:hover {
  fill: #1c3f79 !important;
}

:deep(body:not(.dark-theme)) .user-area .circular-chart .circle {
  stroke: #1474f2 !important;
}

:deep(body:not(.dark-theme)) .user-area .circular-chart .circle:hover {
  stroke: #1c3f79 !important;
}

:deep(body:not(.dark-theme)) .user-area .circular-chart .percentage {
  fill: #1474f2 !important;
}

:deep(body:not(.dark-theme)) .user-area .circular-chart .percentage:hover {
  fill: #1c3f79 !important;
}

/* Cream theme overrides for this view: panels inside main-panel follow a cream palette */
:deep(body.cream-theme) .user-area {
  --cream-bg: #fff8e6;
  /* very light cream */
  --cream-secondary: #fff3d6;
  /* panel background */
  --cream-accent: #d99b3a;
  /* buttons / accents */
  --cream-text-primary: #8a5a00;
  /* heading / primary text */
  --cream-text-secondary: #7a5a2b;
  /* secondary text */
}

:deep(body.cream-theme) .user-area .main-panel {
  /* optional subtle background behind panels */
  background: var(--cream-bg);
}

:deep(body.cream-theme) .user-area .main-panel .panel {
  background: var(--cream-secondary) !important;
  border-color: rgba(138, 90, 0, 0.06) !important;
  color: var(--cream-text-primary) !important;
}

/* (btn theme overrides removed: BaseButton has its own styling) */

:deep(body.cream-theme) .user-area .circular-chart .circle {
  stroke: var(--cream-accent) !important;
}

:deep(body.cream-theme) .user-area .circular-chart .percentage {
  fill: var(--cream-accent) !important;
}

:deep(body.cream-theme) .user-area .panel-row {
  background: rgba(255, 244, 220, 0.45);
  /* soft cream behind the row */
  padding: 8px;
  border-radius: 10px;
  gap: 12px;
}

:deep(body.cream-theme) .user-area .panel-row>.panel {
  /* keep inner panels slightly elevated but blend with cream row */
  box-shadow: none !important;
  border: 1px solid rgba(138, 90, 0, 0.06) !important;
}
</style>
